import { Badge } from '@mantine/core'

import {
  CARRIER_CURATION_STATUS_LABEL,
  CarrierCurationStatus,
} from 'src/graphql/types/carriers'
import { orderPlatforms, voeProviderLabel } from 'src/lib/voeProvider.utils'
import { cn } from 'src/utils'

import type { CarrierSearchResult } from '../carrier.types'
import { carrierName, locationLabel } from '../carrier.utils'

export interface CarrierResultRowProps {
  carrier: CarrierSearchResult
  onSelect: (carrierId: string) => void
  className?: string
}

/**
 * How each rung is drawn in the list. Unlike the carrier header, Unreviewed
 * gets a badge here: the list is where an admin scans for what is still left
 * to look at, so "nothing yet" has to be visible, just quietly.
 */
const CURATION_BADGE: Record<
  CarrierCurationStatus,
  { color: string; variant: 'outline' | 'light' | 'filled' }
> = {
  [CarrierCurationStatus.UNREVIEWED]: { color: 'gray', variant: 'outline' },
  [CarrierCurationStatus.REVIEWED]: { color: 'blue', variant: 'light' },
  [CarrierCurationStatus.LOCKED]: { color: 'orange', variant: 'filled' },
}

const curationBadge = (status: string | null | undefined) => {
  if (!status || !(status in CURATION_BADGE)) return null
  const key = status as CarrierCurationStatus
  return (
    <Badge
      size="xs"
      variant={CURATION_BADGE[key].variant}
      color={CURATION_BADGE[key].color}
      data-testid="curation-badge"
    >
      {CARRIER_CURATION_STATUS_LABEL[key]}
    </Badge>
  )
}

/** One carrier in the search result list. */
const CarrierResultRow = ({
  carrier,
  onSelect,
  className,
}: CarrierResultRowProps) => {
  const location = locationLabel(carrier)
  const platforms = orderPlatforms(carrier.verificationPlatforms)
  const curation = curationBadge(carrier.curationStatus)
  const hasStanding = Boolean(curation) || platforms.length > 0

  return (
    <button
      type="button"
      onClick={() => onSelect(carrier.carrierId)}
      className={cn(
        'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-doubleNickel-gray-50',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-doubleNickel-gray-900">
          {carrierName(carrier)}
        </span>
        {carrier.dotNumber && (
          <span className="font-mono text-xs text-doubleNickel-gray-500">
            DOT {carrier.dotNumber}
          </span>
        )}
      </div>
      {/* Standing: how curated the row is, and how the carrier answers
          verifications. Same colours as the carrier header so a badge means
          the same thing on both screens; platform-only is filled orange like
          Locked because both mean "do not go around this". */}
      {hasStanding && (
        <div className="flex flex-wrap items-center gap-1.5">
          {curation}
          {platforms.map((platform) => (
            <Badge
              key={platform.carrierVerificationPlatformId ?? platform.provider}
              size="xs"
              variant="light"
              data-testid="platform-badge"
            >
              {voeProviderLabel(platform.provider)}
              {platform.preferred && platforms.length > 1 ? ' (preferred)' : ''}
            </Badge>
          ))}
          {carrier.platformOnly && (
            <Badge size="xs" variant="filled" color="orange">
              Platform only
            </Badge>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-doubleNickel-gray-500">
        {location && <span>{location}</span>}
        {carrier.email && <span className="font-mono">{carrier.email}</span>}
        <span
          className={cn(
            carrier.contactCount === 0 && 'text-doubleNickel-warning-700'
          )}
        >
          {carrier.contactCount} contact
          {carrier.contactCount === 1 ? '' : 's'}
        </span>
      </div>
    </button>
  )
}

export default CarrierResultRow
